import {
	Box,
	Button,
	Heading,
	HStack,
	Input,
	Select,
	Stack,
	Text
} from "@chakra-ui/react";
import React from "react";

const TZ_OFFSETS: string[] = [
	"-12:00", "-11:00", "-10:00", "-09:30", "-09:00", "-08:00", "-07:00",
	"-06:00", "-05:00", "-04:00", "-03:30", "-03:00", "-02:00", "-01:00",
	"+00:00",
	"+01:00", "+02:00", "+03:00", "+03:30", "+04:00", "+04:30", "+05:00",
	"+05:30", "+05:45", "+06:00", "+06:30", "+07:00", "+08:00", "+08:45",
	"+09:00", "+09:30", "+10:00", "+10:30", "+11:00", "+12:00", "+12:45",
	"+13:00", "+14:00"
];

const browserOffset = (): string => {
	const offsetMin = -new Date().getTimezoneOffset();
	const sign = offsetMin >= 0 ? "+" : "-";
	const abs = Math.abs(offsetMin);
	const hh = String(Math.floor(abs / 60)).padStart(2, "0");
	const mm = String(abs % 60).padStart(2, "0");
	return `${sign}${hh}:${mm}`;
};

const parseStored = (iso: string | undefined): {local: string; offset: string} => {
	const fallbackOffset = browserOffset();
	if (!iso) {
		return {local: "", offset: fallbackOffset};
	}
	const match = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/);
	if (!match) {
		return {local: "", offset: fallbackOffset};
	}
	let offset = match[3] || fallbackOffset;
	if (offset === "Z") {
		offset = "+00:00";
	} else if (!offset.includes(":")) {
		offset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
	}
	return {local: `${match[1]}T${match[2]}`, offset};
};

const composeIso = (local: string, offset: string): string | undefined => {
	if (!local || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) {
		return undefined;
	}
	return `${local}:00${offset}`;
};

type FieldProps = {
	label: string;
	helper?: string;
	value: string | undefined;
	canEdit: boolean;
	onChange: (next: string | undefined) => void;
};

const TimingField = ({label, helper, value, canEdit, onChange}: FieldProps) => {
	const {local, offset} = parseStored(value);
	const handleLocalChange = (next: string) => {
		onChange(next ? composeIso(next, offset) : undefined);
	};
	const handleOffsetChange = (nextOffset: string) => {
		if (!local) {
			return;
		}
		onChange(composeIso(local, nextOffset));
	};
	return (
		<Box>
			<Text fontSize={"sm"} fontWeight={"medium"} mb={1}>{label}</Text>
			{helper ? (
				<Text fontSize={"xs"} color={"gray.600"} mb={2}>{helper}</Text>
			) : null}
			<HStack spacing={2} maxW={"520px"}>
				<Input
					type={"datetime-local"}
					value={local}
					onChange={(event) => handleLocalChange(event.target.value)}
					isDisabled={!canEdit}
				/>
				<Select
					value={offset}
					onChange={(event) => handleOffsetChange(event.target.value)}
					isDisabled={!canEdit || !local}
					maxW={"110px"}
				>
					{TZ_OFFSETS.includes(offset) ? null : (
						<option value={offset}>{offset}</option>
					)}
					{TZ_OFFSETS.map((option) => (
						<option key={option} value={option}>{option}</option>
					))}
				</Select>
				{local ? (
					<Button
						variant={"ghost"}
						size={"sm"}
						onClick={() => onChange(undefined)}
						isDisabled={!canEdit}
					>
						Clear
					</Button>
				) : null}
			</HStack>
		</Box>
	);
};

type TimingsSettingsTabProps = {
	canEdit: boolean;
	downloadFrom: string | undefined;
	downloadTo: string | undefined;
	playFrom: string | undefined;
	playTo: string | undefined;
	error?: string;
	onDownloadFromChange: (value: string | undefined) => void;
	onDownloadToChange: (value: string | undefined) => void;
	onPlayFromChange: (value: string | undefined) => void;
	onPlayToChange: (value: string | undefined) => void;
};

const TimingsSettingsTab = ({
	canEdit,
	downloadFrom,
	downloadTo,
	playFrom,
	playTo,
	error,
	onDownloadFromChange,
	onDownloadToChange,
	onPlayFromChange,
	onPlayToChange
}: TimingsSettingsTabProps) => {
	return (
		<Stack spacing={6}>
			<Box>
				<Heading size={"sm"} mb={1}>Download window</Heading>
				<Text fontSize={"sm"} color={"gray.600"} mb={3}>
					When the space can be downloaded. Outside this window the configuration link returns an error
					and the space is hidden from the Public Spaces page.
				</Text>
				<Stack spacing={4}>
					<TimingField
						label={"From"}
						value={downloadFrom}
						canEdit={canEdit}
						onChange={onDownloadFromChange}
					/>
					<TimingField
						label={"To"}
						value={downloadTo}
						canEdit={canEdit}
						onChange={onDownloadToChange}
					/>
				</Stack>
			</Box>
			<Box>
				<Heading size={"sm"} mb={1}>Play window</Heading>
				<Text fontSize={"sm"} color={"gray.600"} mb={3}>
					When the downloaded space can be played. Enforced by the app.
				</Text>
				<Stack spacing={4}>
					<TimingField
						label={"From"}
						value={playFrom}
						canEdit={canEdit}
						onChange={onPlayFromChange}
					/>
					<TimingField
						label={"To"}
						value={playTo}
						canEdit={canEdit}
						onChange={onPlayToChange}
					/>
				</Stack>
			</Box>
			{error ? (
				<Text fontSize={"sm"} color={"red.500"}>{error}</Text>
			) : null}
			{!canEdit ? (
				<Text fontSize={"sm"} color={"orange.600"}>
					Area settings can only be updated while editing a draft.
				</Text>
			) : null}
		</Stack>
	);
};

export default TimingsSettingsTab;
